import "#/legacy/namespaces.js";
import "#/shared/api/api-host-global.js";
import "#legacy/shared/i18n.js";
import "#legacy/application/google_maps_loader.js";
import "#legacy/utilities/geolocate.js";
import "#legacy/admin/components/spotlight_search.js";
import "#finder-legacy/physician_finder/helpers/filter_app_readiness.js";
import { TestId } from "#/test-ids";

const LOCATION_SETTINGS_URL = window.Sparkle.apiHost
  ? window.Sparkle.apiHost + window.SparkleConfig.routes.embed.filterSettings
  : window.SparkleConfig.routes.embed.filterSettings;
const REMOTE_IP_GEOLOCATION_URL = window.Sparkle.apiHost
  ? window.Sparkle.apiHost + window.SparkleConfig.routes.embed.geocodeAddresses
  : window.SparkleConfig.routes.embed.geocodeAddresses;
const DEFAULT_DISTANCE_VALUES = [5, 10, 15, 20, 25];

DoctorFinder.Views.LocationSearch = class LocationSearch extends Sparkle.SpotlightSearch {
  static ANY_DISTANCE = "any";

  static {
    this.prototype.I18n = new Sparkle.I18n({
      doctor_finder: {
        labels: {
          filters: {
            distance: "Distance in Miles",
            distance_slider: "Maximum distance (in miles)",
          },
          filter_options: {
            distance: {
              any: "Any Distance",
            },
          },
          common_locations: "Common Locations",
        },
      },
    });
  }

  constructor(trigger, options = {}) {
    const _options = options || {};
    const _defaultDistance = _options.filterOptions?.distance?.default;

    super(
      null,
      _.defaults(_options, {
        minQueryLength: 0,
        minPhraseLength: 1,
        autofocus: true,
        position: false,
        details: false,
        status: false,
        hideOnEnterWithoutSelection: false, // Don't allow pressing enter to close the search unless a result is picked
      }),
    );

    this.handleInputInteraction = this.handleInputInteraction.bind(this);
    this.handleInputUnfocussed = this.handleInputUnfocussed.bind(this);
    this.handleTriggerKeyPressed = this.handleTriggerKeyPressed.bind(this);
    this.handleItemSelected = this.handleItemSelected.bind(this);
    this.handleDistanceChange = this.handleDistanceChange.bind(this);
    this.handleDistanceSliderChange = this.handleDistanceSliderChange.bind(this);
    this._normalizeSliderDistance = this._normalizeSliderDistance.bind(this);
    this._syncDistanceSelect = this._syncDistanceSelect.bind(this);
    this.handleGeolocate = this.handleGeolocate.bind(this);
    this.handleGeolocateSuccess = this.handleGeolocateSuccess.bind(this);
    this.handleGeolocateFailure = this.handleGeolocateFailure.bind(this);
    this.handleGoogleMapsLoaded = this.handleGoogleMapsLoaded.bind(this);
    this.handleWizardStepVisibChange = this.handleWizardStepVisibChange.bind(this);
    this.closeOnInteractWithOther = this.closeOnInteractWithOther.bind(this);
    this.render = this.render.bind(this);
    this.getState = this.getState.bind(this);
    this.setState = this.setState.bind(this);
    this.markExplicitlySelected = this.markExplicitlySelected.bind(this);
    this.show = this.show.bind(this);
    this.hide = this.hide.bind(this);
    this.clear = this.clear.bind(this);
    this.updateResults = this.updateResults.bind(this);
    this.wizardStepVisible = this.wizardStepVisible.bind(this);
    this.isWizardStepVisible = this.isWizardStepVisible.bind(this);
    this._renderCommonPlaces = this._renderCommonPlaces.bind(this);
    this.fetchLocationSettings = this.fetchLocationSettings.bind(this);
    this._fetch = this._fetch.bind(this);
    this.loadGoogleMapsAutocomplete = this.loadGoogleMapsAutocomplete.bind(this);
    this.fetchWhenGoogleMapsAutocompleteReady = this.fetchWhenGoogleMapsAutocompleteReady.bind(this);
    this._highlightResults = this._highlightResults.bind(this);
    this._renderSearchResults = this._renderSearchResults.bind(this);
    this._isSearching = this._isSearching.bind(this);
    this._triggerChange = this._triggerChange.bind(this);
    this.fetchGeolocationIfNeeded = this.fetchGeolocationIfNeeded.bind(this);
    this.fetchDeviceGeolocation = this.fetchDeviceGeolocation.bind(this);
    this.fetchRemoteGeolocationIfNeeded = this.fetchRemoteGeolocationIfNeeded.bind(this);
    this.fetchRemoteGeolocation = this.fetchRemoteGeolocation.bind(this);
    this.trigger = trigger;
    this.options = _options;
    this.I18n = new Sparkle.I18n(this.I18n).merge(options.i18n);
    this.commonPlaces = this.options.commonPlaces;
    this.placeSortOrder = this.options.placeSortOrder;
    this.askDeviceForLocation = this.options.askDeviceForLocation;
    this.geocodeIP = this.options.geocodeIP;
    this.filterOptions = this.options.filterOptions;
    this.defaultDistance = _defaultDistance;
    // `filter_options.distance.control: slider` swaps the option select for an
    // <sp-number-slider> that snaps through the distance values (the largest
    // entry is the cap).
    this.distanceSliderMode = this.filterOptions?.distance?.control === "slider";
    // Offer the "Any Distance" option unless the client opts out
    // (filter_options.distance.allow_any: false), e.g. when a location — and thus
    // a radius — is always required.
    this.allowAnyDistance = this.filterOptions?.distance?.allow_any !== false;
    this.deferGoogleMapsAutocomplete = !!(
      this.options.deferGoogleMapsAutocomplete || this.filterOptions?.near?.defer_google_maps_autocomplete
    );
    this.state = {
      distance: this.options.distance || this.defaultDistance,
      location: this.options.location,
      locationDisplayName: this.options.locationDisplayName,
      lat: this.options.lat,
      lng: this.options.lng,
    };
    this.fetchLocationSettings();
    this.locationSearchOptions = jQuery.extend({ inputPlaceholder: "Search" }, options);

    // Slice so pushing the default distance below can't mutate the shared config
    // (filterOptions is reused across LocationSearch instances) or the module default.
    this.distanceValues = (
      options.distanceValues ||
      this.filterOptions?.distance?.values ||
      DEFAULT_DISTANCE_VALUES
    ).slice();

    // The slider's list is the fixed set of snap stops; only the select needs an
    // option inserted for a default that isn't in the configured list.
    if (!this.distanceSliderMode && this.defaultDistance && !this.distanceValues.includes(this.defaultDistance)) {
      this.distanceValues.push(this.defaultDistance);
      this.distanceValues.sort((a, b) => a - b);
    }

    // With no "Any Distance" option there must always be a concrete radius, so
    // seed one — the configured default, else the smallest option — when none was
    // supplied OR when a persisted/URL value is the "any" sentinel (which would
    // otherwise submit a no-radius search with no matching option to display).
    if (!this.allowAnyDistance && (!this.state.distance || this.state.distance === this.constructor.ANY_DISTANCE)) {
      this.state.distance = this.defaultDistance || this.distanceValues[0];
    }

    this.geocodeLocationPanel = jQuery("<div>", { class: "geocode-location-panel" });
    this.geocodeLocationTrigger = jQuery("<a>", { tabindex: 0, role: "button" }).text(
      this.I18n.t("doctor_finder.placeholders.geolocate"),
    );
    this.geocodeLocationPanel.prepend(this.geocodeLocationTrigger);

    if (this.distanceSliderMode) {
      this._normalizeSliderDistance();

      this.distanceSlider = jQuery("<sp-number-slider>", {
        label: this.I18n.t("doctor_finder.labels.filters.distance_slider"),
      });
      this.distanceSlider.attr("values", JSON.stringify(this.distanceValues));
      this.distanceSlider.attr("value", this.state.distance);
      this.distanceSlider[0].setAttribute("input-test-id", TestId.finder.distanceSliderInput);
      this.distanceSlider.on("change", this.handleDistanceSliderChange);
    } else {
      this.distanceSelect = document.createElement("sp-select");
      this.distanceSelect.addEventListener("change", this.handleDistanceChange);
    }

    this.listHeader = jQuery("<label>", { class: "list-header" });

    // BEHAVIOUR

    jQuery(this.trigger).on("click", this.show);
    jQuery(this.trigger).on("keydown", this.handleTriggerKeyPressed);

    if (!this.deferGoogleMapsAutocomplete) {
      this.loadGoogleMapsAutocomplete();
    }
    this.geocodeLocationTrigger.on("click", this.handleGeolocate);
    this.input.on("blur", this.handleInputUnfocussed);

    // INITIALIZE
    if (!this.options.input) {
      this.setQuery(this.state.location);
    } // If the input is self-managed, make sure its value matches the initial state
    if (this.options.alwaysShowResults) {
      this.show();
    }
  }

  // EVENT HANDLERS

  handleInputInteraction(event) {
    if (event.type === "input") {
      this.googleMapsAutocompleteRequested = true;
    }
    return super.handleInputInteraction(event);
  }

  handleInputUnfocussed(event) {
    return this.spotlight.clearFocus(); // When the spotlight search is unfocussed, clear highlighted result
  }

  handleTriggerKeyPressed(event) {
    if (event.which === 40) {
      // Down arrow
      return this.show();
    }
  }

  handleItemSelected(event, selectedItem) {
    this.explicitlySelected = true; // Only explicit list selections are persisted; geolocation is not.
    this.setState({
      location: selectedItem.value,
      locationDisplayName: selectedItem.selectedText || selectedItem.name,
      distance: this.state.distance,
    });

    super.handleItemSelected(event, selectedItem);
    // Mark the change as a fresh pick from the list — `explicitlySelected` alone can't (it stays true after a seed/restore)
    return this._triggerChange({ listSelection: true });
  }

  // The select's prompt row is Any Distance, which it reports as no selection.
  handleDistanceChange(event) {
    this.state.distance = event.currentTarget.value || this.constructor.ANY_DISTANCE;
    return this._triggerChange();
  }

  // The slider's change is a CustomEvent whose detail carries the emit payload
  // array; it fires with the committed numeric value on thumb release or a
  // numeric-input edit, never mid-drag.
  handleDistanceSliderChange(event) {
    const [distance] = event.originalEvent?.detail || [];
    if (distance == null) {
      return;
    }
    this.state.distance = distance;
    return this._triggerChange();
  }

  handleGeolocate() {
    return this.fetchDeviceGeolocation();
  }

  handleGeolocateSuccess(location, status) {
    this.explicitlySelected = false; // Device geolocation is not an explicit selection; don't persist it.
    this.geocodeLocationTrigger.text(this.I18n.t("doctor_finder.placeholders.geolocate"));

    this.setState({
      lat: Number.parseFloat(location.coords.latitude.toFixed(3)),
      lng: Number.parseFloat(location.coords.longitude.toFixed(3)),
      location: this.I18n.t("doctor_finder.placeholders.geolocated"),
      locationDisplayName: this.I18n.t("doctor_finder.placeholders.geolocated"),
      distance: this.state.distance,
    });

    // Hide when geolocation is done as if we've just selected a location
    if (!this.options.keepVisibleOnGeolocation) {
      this.hide();
    }
    return this._triggerChange();
  }

  handleGeolocateFailure(notify = true) {
    this.geocodeLocationTrigger.text(this.I18n.t("doctor_finder.placeholders.geolocate"));
    if (notify) {
      return window.alert(this.I18n.t("doctor_finder.placeholders.geolocate_error"));
    }
  }

  handleGoogleMapsLoaded() {
    if (this.autocompleteService) {
      return;
    }

    this.autocompleteService = new google.maps.places.AutocompleteService();

    // Use publishing domain-specific geocoder bounds if available, otherwise fall back to defaults
    const geocoderBounds = window.Sparkle?.Config?.googleMaps?.geocoderBounds;
    if (
      geocoderBounds &&
      geocoderBounds.sw_lat &&
      geocoderBounds.sw_lng &&
      geocoderBounds.ne_lat &&
      geocoderBounds.ne_lng
    ) {
      return (this.bounds = new google.maps.LatLngBounds(
        new google.maps.LatLng(geocoderBounds.sw_lat, geocoderBounds.sw_lng),
        new google.maps.LatLng(geocoderBounds.ne_lat, geocoderBounds.ne_lng),
      ));
    } else {
      const _defaultBounds = window.SparkleConfig.doctorFinder.defaultGeocoderBounds;
      return (this.bounds = new google.maps.LatLngBounds(
        new google.maps.LatLng(_defaultBounds.sw_lat, _defaultBounds.sw_lng),
        new google.maps.LatLng(_defaultBounds.ne_lat, _defaultBounds.ne_lng),
      ));
    }
  }

  handleWizardStepVisibChange() {
    if (this.isWizardStepVisible()) {
      return this.fetchGeolocationIfNeeded(false);
    }
  }

  closeOnInteractWithOther(event) {
    if (jQuery(event.target).closest(this.trigger).length) {
      return;
    }
    return super.closeOnInteractWithOther(event);
  }

  // RENDERING

  render() {
    super.render(...arguments);

    const searchInputPanel = jQuery("<div>", { class: "search-panel form-group search-location__panel" });

    if (this.distanceSliderMode) {
      // The slider renders its own label, so the group label is omitted.
      searchInputPanel.append(this.distanceSlider);
    } else {
      const milesSuffix = this.I18n.t("doctor_finder.labels.filter_options.distance.miles_suffix");
      const withinPrefix = this.I18n.t("doctor_finder.labels.filter_options.distance.within_prefix");
      this.distanceSelect.items = this.distanceValues.map((miles) => ({
        title: `${miles} ${milesSuffix}`,
        value: String(miles),
      }));
      const attributes = {
        "prepend-selected-with": withinPrefix,
        "aria-label": this.I18n.t("doctor_finder.labels.filters.distance"),
        "data-testid": TestId.finder.landingDistance,
        "item-test-id": TestId.finder.landingDistanceOption,
        "selected-icon": "check-filled",
        "no-sheet": "",
        "no-caret-flip": "",
        "caret-variant": "image",
        pointer: "",
        "no-restore-focus": "",
      };
      Object.entries(attributes).forEach(([name, value]) => this.distanceSelect.setAttribute(name, value));
      this._syncDistanceSelect();
      if (this.allowAnyDistance) {
        this.distanceSelect.setAttribute("prompt", this.I18n.t("doctor_finder.labels.filter_options.distance.any"));
      }

      const groupLabel = jQuery('<label class="distance filter-group-label"></label>').text(
        this.I18n.t("doctor_finder.labels.filters.distance"),
      );
      searchInputPanel.append(groupLabel, this.distanceSelect);
    }

    searchInputPanel.append(this.geocodeLocationPanel);
    searchInputPanel.append(
      jQuery('<label class="from filter-group-label"></label>').text(this.I18n.t("doctor_finder.labels.from")),
      this.input.attr({ placeholder: this.locationSearchOptions.inputPlaceholder }),
    );

    const listWrapper = jQuery("<div>", { class: "list-wrapper" });
    listWrapper.append(this.listHeader);

    this.spotlight.el.prepend(searchInputPanel, listWrapper);

    this.spotlight.itemsListContainer.appendTo(listWrapper);
    this.spotlight.itemsListContainer.addClass("search-results");

    return this;
  }

  // API

  getState() {
    return {
      ...this.state,
      explicitlySelected: !!this.explicitlySelected,
    };
  }

  // The slider only offers the configured stops — align the state with what it
  // shows. A numeric distance (e.g. a deep-linked ?distance=60) snaps to the
  // nearest stop; a missing distance or a persisted/URL 'any' would keep
  // searching an unlimited radius while the control displays its largest value,
  // so it becomes the maximum.
  _normalizeSliderDistance() {
    if (!this.distanceValues.length) {
      return;
    }
    const distance = parseInt(this.state.distance);
    if (isFinite(distance)) {
      this.state.distance = this.distanceValues.reduce((best, stop) =>
        Math.abs(stop - distance) < Math.abs(best - distance) ? stop : best,
      );
    } else {
      this.state.distance = Math.max(...this.distanceValues);
    }
  }

  setState(state) {
    this.state = state;
    if (this.distanceSliderMode) {
      this._normalizeSliderDistance();
      this.distanceSlider.attr("value", this.state.distance);
    } else {
      this._syncDistanceSelect();
    }
    this.setQuery(this.state.location);
    if (this.options.alwaysShowResults || this.spotlight.showing) {
      return this._renderCommonPlaces();
    }
  }

  // Mark the current state as an explicit selection so subsequent changes (e.g. distance) are persisted.
  // Used when restoring a previously persisted location, which was itself explicitly selected.
  markExplicitlySelected() {
    this.explicitlySelected = true;
  }

  show() {
    return this._renderCommonPlaces().then(() => {
      this.spotlight.show();
      this._focusFirstControl();
      return this.spotlight.clearFocus();
    }); // On initialize we don't want to highlight results because of the complexity of this select
  }

  hide() {
    return this.spotlight.hide();
  }

  clear() {
    this.explicitlySelected = false;
    return this.setState({});
  }

  updateResults() {
    if (this.autocompleteService && this.input.val()) {
      return this._fetch();
    } else if (this.input.val() && this.deferGoogleMapsAutocomplete && this.googleMapsAutocompleteRequested) {
      return this.fetchWhenGoogleMapsAutocompleteReady();
    } else {
      return this._renderCommonPlaces();
    }
  }

  wizardStepVisible(wizardStepVisible) {
    this.__wizardStepVisible = wizardStepVisible;
    return this.handleWizardStepVisibChange();
  }

  isWizardStepVisible() {
    return this.__wizardStepVisible;
  }

  // PRIVATE

  _renderCommonPlaces() {
    return this.fetchLocationSettings().then(() => {
      // Common Locations are a static list, independent of the input text. Only bail when the user is
      // actively typing a *new* query (input differs from the committed location) so we don't clobber
      // their autocomplete results; a committed/pre-filled location must still render the list. Checked
      // at resolve time so a programmatic input sync that lands during the fetch doesn't suppress it.
      if (this.input.val() && this.input.val() !== this.state?.location) {
        return;
      }

      if (this.commonPlaces) {
        this.listHeader.text(this.I18n.t("doctor_finder.labels.common_locations"));
        this._renderResults(this.commonPlaces);
      } else {
        this.listHeader.text("");
        this._renderResults([]);
      }

      // Common Locations is a static suggestion list, not a response to a typed query, so the first entry
      // must not be pre-highlighted. setItems auto-highlights index 0 because of `autofocus: true` (which we
      // want for typed search results); undo that here so merely focusing the input doesn't highlight an item.
      return this.spotlight.clearFocus();
    });
  }

  fetchLocationSettings() {
    if (this.commonPlaces && this.placeSortOrder) {
      const deferred = new jQuery.Deferred();
      deferred.resolve([this.commonPlaces, this.placeSortOrder]);
      return deferred.promise();
    }

    if (this.locationSettingsFetch) {
      return this.locationSettingsFetch.promise();
    }

    this.locationSettingsFetch = new jQuery.Deferred();
    const locationSettingsFetch = this.locationSettingsFetch;

    jQuery
      .getJSON(LOCATION_SETTINGS_URL)
      .done((data) => {
        if (!this.commonPlaces) {
          this.commonPlaces = data != null ? data.common_places : undefined;
        }
        this.placeSortOrder = data != null ? data.place_suggestion_sort_order : undefined;
        return locationSettingsFetch.resolve([this.commonPlaces, this.placeSortOrder]);
      })
      .fail(() => {
        // Drop the cached deferred so the next open retries the fetch, and resolve (rather than leave
        // it pending) so the in-flight render completes instead of hanging the Common Locations panel.
        this.locationSettingsFetch = null;
        return locationSettingsFetch.resolve([this.commonPlaces, this.placeSortOrder]);
      });

    return this.locationSettingsFetch.promise();
  }

  loadGoogleMapsAutocomplete(callback) {
    if (this.googleMapsAutocompleteLoad == null) {
      const deferred = new jQuery.Deferred();
      Sparkle.GoogleMaps.load(() => {
        this.handleGoogleMapsLoaded();
        return deferred.resolve();
      });
      this.googleMapsAutocompleteLoad = deferred.promise();
    }

    if (callback != null) {
      this.googleMapsAutocompleteLoad.done(callback);
    }
    return this.googleMapsAutocompleteLoad;
  }

  fetchWhenGoogleMapsAutocompleteReady() {
    if (this.googleMapsAutocompleteFetchPending) {
      return;
    }

    this.googleMapsAutocompleteFetchPending = true;
    return this.loadGoogleMapsAutocomplete(() => {
      this.googleMapsAutocompleteFetchPending = false;
      if (this.input.val()) {
        return this._fetch();
      }
    });
  }

  _fetch() {
    return this.autocompleteService.getPlacePredictions(
      {
        input: this.input.val(),
        bounds: this.bounds,
        types: ["geocode"],
        componentRestrictions: { country: "us" },
      },
      this._renderSearchResults,
    );
  }

  _highlightResults() {}
  // no-op

  _renderSearchResults(predictions = [], status) {
    const sortOrder = this.placeSortOrder || [];
    predictions = predictions.sort((a, b) => {
      const aOrder = sortOrder.indexOf(a.structured_formatting.secondary_text.replace(/, USA$/, ""));
      const bOrder = sortOrder.indexOf(b.structured_formatting.secondary_text.replace(/, USA$/, ""));

      if (aOrder < 0 && bOrder < 0) {
        return 0;
      }
      if (aOrder < 0) {
        return 1;
      }
      if (bOrder < 0) {
        return -1;
      }
      return aOrder - bOrder;
    });

    this.listHeader.text(this.I18n.t("doctor_finder.labels.search_results"));
    const data = [];

    if (status === google.maps.places.PlacesServiceStatus.OK) {
      jQuery.each(predictions, (index, prediction) => {
        // Submit the full description so the geocoder gets the unambiguous value (Google returns US
        // places with a ", USA" suffix), but strip that suffix from the display name — the sites are
        // US-only, so ", USA" is redundant noise in the list and the selected-location label.
        const value = prediction.description;
        const name = value.replace(/, USA$/, "");
        return data.push({ name, value, selectedText: prediction.structured_formatting.main_text });
      });
    }

    return this.spotlight.setItems(data);
  }

  _isSearching() {
    return !!this.input.val().length;
  }

  // Focuses the first focusable control in the panel. The radius select takes focus once it is
  // mount-ready, and only while the panel is still showing and focus has not moved meanwhile.
  _focusFirstControl() {
    const control = this.el[0].querySelector(
      'sp-select, [tabindex]:not([tabindex="-1"]), input, select, textarea, button',
    );

    if (control?.localName !== "sp-select") {
      control?.focus();
      return;
    }

    const focusedBeforeWait = document.activeElement;

    DoctorFinder.Helpers.FilterAppReadiness.whenOperational(control).then(
      () => {
        if (this.spotlight.showing && document.activeElement === focusedBeforeWait) {
          control.focus();
        }
      },
      () => {},
    );
  }

  // Shows the current radius in the select. Any Distance is its prompt row, which it shows while it
  // holds no value. The `value` attribute is the select's default, and a mount-ready select also
  // takes the live `value`, which a pick has replaced.
  _syncDistanceSelect() {
    const { distance } = this.state;
    const value =
      distance == null || distance === "" || distance === this.constructor.ANY_DISTANCE ? null : String(distance);

    if (value == null) {
      this.distanceSelect.removeAttribute("value");
    } else {
      this.distanceSelect.setAttribute("value", value);
    }

    if (this.distanceSelect.hasAttribute("data-sp-ce-ready")) {
      this.distanceSelect.value = value;
    }
  }

  // `extras` ride the emitted change event only (not `getState()`): transient, per-event
  // metadata such as `listSelection` that must not stick to the persisted state.
  _triggerChange(extras) {
    const state = jQuery.extend(this.getState(), extras);
    if (this.options.onChange != null) {
      this.options.onChange(state);
    }
    return this._triggerEvent("change", state);
  }

  fetchGeolocationIfNeeded(notify) {
    if (this.state.location) {
      return;
    }

    if (this.askDeviceForLocation) {
      return this.fetchDeviceGeolocation(notify).always(this.fetchRemoteGeolocationIfNeeded);
    } else {
      return this.fetchRemoteGeolocationIfNeeded();
    }
  }

  fetchDeviceGeolocation(notify) {
    this.geocodeLocationTrigger.text(this.I18n.t("doctor_finder.placeholders.geolocating"));
    return Sparkle.Utilities.geolocate().then(this.handleGeolocateSuccess, () => this.handleGeolocateFailure(notify));
  }

  fetchRemoteGeolocationIfNeeded() {
    if (this.state.location || !this.geocodeIP) {
      return;
    }
    return this.fetchRemoteGeolocation();
  }

  fetchRemoteGeolocation() {
    return jQuery.getJSON(REMOTE_IP_GEOLOCATION_URL).then((data) => {
      if (data) {
        this.explicitlySelected = false; // IP geolocation is not an explicit selection; don't persist it.
        this.setState({ location: data.city, locationDisplayName: data.address });
      }
      return this._triggerChange();
    });
  }
};
